<template>
  <b-modal
    v-model="showModal"
    size="md"
    :style="{ zIndex: 200 }"
    :modal-class="(loading || isSubmitting) ? 'loading' : ''"
    no-close-on-backdrop
    centered
  >
    <template v-slot:modal-header="{ cancel }">
      <h5 class="modal-title">Change password</h5>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click.prevent="cancel"
      >
        <span class="material-icons"> close </span>
      </button>
    </template>

    <!-- Custom form layout to insert validation between fields -->
    <form class="custom-password-form" @submit.prevent="submitPasswordForm">
      <!-- Current password field (conditional) -->
      <div v-if="showCurrentPasswordField" class="form-group col-12">
        <label class="form-label">Enter password</label>
        <div class="password-input-wrapper">
          <input
            :type="showCurrentPassword ? 'text' : 'password'"
            class="form-control"
            placeholder="password"
            v-model="formData.currentPassword"
            @input="onCurrentPasswordInput"
          />
          <button
            type="button"
            class="password-toggle-btn"
            @click="togglePasswordVisibility('current')"
          >
            <span class="material-symbols-outlined">
              {{ showCurrentPassword ? "visibility_off" : "visibility" }}
            </span>
          </button>
        </div>
      </div>

      <!-- New password field -->
      <div class="form-group col-12">
        <label class="form-label">Enter new password</label>
        <div class="password-input-wrapper">
          <input
            :type="showNewPassword ? 'text' : 'password'"
            class="form-control"
            placeholder="password"
            v-model="formData.newPassword"
            @input="onNewPasswordInput"
          />
          <button
            type="button"
            class="password-toggle-btn"
            @click="togglePasswordVisibility('new')"
          >
            <span class="material-symbols-outlined">
              {{ showNewPassword ? "visibility_off" : "visibility" }}
            </span>
          </button>
        </div>
      </div>

      <!-- Password Requirements Display - right after new password field -->
      <div
        class="password-requirements-section mt-2 mb-3"
        style="padding-left: 15px"
      >
        <PasswordRequirements
          :hasMinLength="getPasswordRequirementState('hasMinLength')"
          :hasUppercaseAndLowercase="
            getPasswordRequirementState('hasUppercaseAndLowercase')
          "
          :hasNumbers="getPasswordRequirementState('hasNumbers')"
          :hasSpecialChars="getPasswordRequirementState('hasSpecialChars')"
          :useGridLayout="true"
          :showTitle="false"
        />
      </div>

      <!-- Confirm password field -->
      <div class="form-group col-12">
        <label class="form-label">Enter new password again</label>
        <div class="password-input-wrapper">
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            class="form-control"
            placeholder="password"
            v-model="formData.confirmPassword"
            @input="onConfirmPasswordInput"
          />
          <button
            type="button"
            class="password-toggle-btn"
            @click="togglePasswordVisibility('confirm')"
          >
            <span class="material-symbols-outlined">
              {{ showConfirmPassword ? "visibility_off" : "visibility" }}
            </span>
          </button>
        </div>
      </div>

      <!-- Password Match Validation -->
      <div
        class="password-match-section mt-2 mb-3"
        style="padding-left: 15px"
      >
        <PasswordRequirementRow :isValid="getPasswordMatchState()">
          Must match the new password.
        </PasswordRequirementRow>
      </div>
    </form>

    <template v-slot:modal-footer="{ cancel }">
      <b-button
        variant="link"
        class="button cancel-button"
        @click.prevent="cancel"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        class="button"
:disabled="!isPasswordFormValid || loading || isSubmitting"
        @click.prevent="submitPasswordForm"
      >
        Save changes
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import PasswordRequirements from '@components/sign-up/PasswordRequirements.vue'
import PasswordRequirementRow from '@components/sign-up/PasswordRequirementRow.vue'
import { validatePassword } from '@components/sign-up/validatePassword'

export default {
  name: 'ChangePasswordModal',
  components: {
    PasswordRequirements,
    PasswordRequirementRow
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    showCurrentPasswordField: {
      type: Boolean,
      default: true
    },
    onSubmit: {
      type: Function,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formData: {
        currentPassword: null,
        newPassword: null,
        confirmPassword: null
      },

      // Password validation properties
      passwordValidation: {
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumbers: false,
        hasSpecialChars: false
      },
      showPasswordRequirements: true,

      // Password visibility toggles
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,

      // Internal loading state
      isSubmitting: false
    }
  },
  computed: {
    showModal: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },

    isPasswordFormValid () {
      const currentPassword = this.formData.currentPassword
      const newPassword = this.formData.newPassword
      const confirmPassword = this.formData.confirmPassword

      // Check current password only if field is shown
      const hasCurrentPassword = this.showCurrentPasswordField
        ? currentPassword && currentPassword.trim() !== ''
        : true

      // Check if all required fields have values
      const hasAllFields =
        hasCurrentPassword &&
        newPassword &&
        newPassword.trim() !== '' &&
        confirmPassword &&
        confirmPassword.trim() !== ''

      // Check if new password meets all requirements
      const passwordRequirementsMet =
        this.passwordValidation.hasMinLength &&
        this.passwordValidation.hasUppercase &&
        this.passwordValidation.hasLowercase &&
        this.passwordValidation.hasNumbers &&
        this.passwordValidation.hasSpecialChars

      // Check if passwords match
      const passwordsMatch = newPassword === confirmPassword

      return hasAllFields && passwordRequirementsMet && passwordsMatch
    }
  },
  watch: {
    value (newValue) {
      if (newValue) {
        // Opening modal - reset to clean state
        this.resetPasswordValidation()
      } else {
        // Closing modal - also reset to clean state
        this.resetPasswordValidation()
      }
    },
    'formData.newPassword': {
      handler (newValue) {
        this.validateNewPassword(newValue)
        this.checkPasswordMatch()
      },
      immediate: true
    },
    'formData.confirmPassword': {
      handler () {
        this.checkPasswordMatch()
      },
      immediate: true
    }
  },
  methods: {
    onCurrentPasswordInput (event) {
      const value = event.target ? event.target.value : event
      this.formData.currentPassword = value
    },

    onNewPasswordInput (event) {
      const value = event.target ? event.target.value : event
      this.formData.newPassword = value
      this.validateNewPassword(value)
      this.checkPasswordMatch()
    },

    onConfirmPasswordInput (event) {
      const value = event.target ? event.target.value : event
      this.formData.confirmPassword = value
      this.checkPasswordMatch()
    },

    resetPasswordValidation () {
      // Reset form field values
      this.formData = {
        currentPassword: null,
        newPassword: null,
        confirmPassword: null
      }

      // Reset validation states
      this.passwordValidation = {
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumbers: false,
        hasSpecialChars: false
      }

      // Reset validation display states
      this.showPasswordRequirements = true

      // Reset password visibility states
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
    },

    validateNewPassword (newPassword) {
      // Always reset validation when password is empty or null
      if (!newPassword || newPassword.trim() === '') {
        this.passwordValidation = {
          hasMinLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumbers: false,
          hasSpecialChars: false
        }
        return
      }

      this.passwordValidation = validatePassword(newPassword)
    },

    checkPasswordMatch () {
      // This method is now mainly for triggering reactivity
      // The actual matching logic is handled in getPasswordMatchState()
      // Force reactivity update by accessing the values without storing them
      void this.formData.newPassword
      void this.formData.confirmPassword
    },

    getPasswordRequirementState (requirement) {
      const newPassword = this.formData.newPassword

      // Always show bullet when no password is entered, regardless of validation state
      if (!newPassword || newPassword.trim() === '') {
        return null // Show bullet when no password entered
      }

      if (requirement === 'hasUppercaseAndLowercase') {
        return (
          this.passwordValidation.hasUppercase &&
          this.passwordValidation.hasLowercase
        )
      }

      return this.passwordValidation[requirement]
    },

    getPasswordMatchState () {
      const newPassword = this.formData.newPassword
      const confirmPassword = this.formData.confirmPassword

      // Show bullet when either field is empty
      if (
        !newPassword ||
        newPassword.trim() === '' ||
        !confirmPassword ||
        confirmPassword.trim() === ''
      ) {
        return null // Show bullet when fields are empty
      }

      // Return true if passwords match, false if they don't
      return newPassword === confirmPassword
    },

    togglePasswordVisibility (field) {
      switch (field) {
        case 'current':
          this.showCurrentPassword = !this.showCurrentPassword
          break
        case 'new':
          this.showNewPassword = !this.showNewPassword
          break
        case 'confirm':
          this.showConfirmPassword = !this.showConfirmPassword
          break
      }
    },

    async submitPasswordForm () {
      if (!this.isPasswordFormValid || this.isSubmitting) return

      this.isSubmitting = true

      try {
        // Prepare data for submission
        const submitData = {
          password: this.formData.newPassword
        }

        // Add current password only if field is shown
        if (this.showCurrentPasswordField) {
          submitData.old_password = this.formData.currentPassword
        }

        // Call the provided submit handler
        await this.onSubmit(submitData)
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .modal .modal-dialog {
  max-width: 700px;
}
// Password field styles for change password modal
.password-input-wrapper {
  position: relative;

  .form-control {
    padding-right: 40px; // Make space for the eye icon
  }

  .password-toggle-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #6c757d;
    transition: color 0.2s ease;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #495057;
    }

    &:focus {
      outline: none;
    }

    .material-symbols-outlined {
      font-size: 18px;
    }
  }
}

.custom-password-form {
  .form-group {
    margin-bottom: 1rem;

    .form-label {
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
  }
}

.password-requirements-section,
.password-match-section, .form-group.col-12 {
  padding-left: 0 !important;
}

// Modal footer button spacing
.cancel-button {
  margin-right: 11px;
}
</style>
